import type { IReportType, IReportAhuRow } from 'app/services/reportsBoardApi';

/**
 * Phase 5a — report scope classes + canGenerate truth table + conditional-UI
 * derivations, ported 1:1 from the Backbone report board.
 *
 * The legacy board encodes report "scope" as CSS classes on the menu buttons
 * (menu.tmpl:15,30) and every gating decision is a jQuery hasClass() on the
 * `.current_report` button. This module reproduces the exact class list and
 * hasClass-equivalent checks so the truth tables read the same way.
 */

/** Section-scoped reports whose menu buttons also HIDE (not just disable) when
 *  the selected AHU lacks the section (menu.js:5,141). */
export const SECTION_REPORTS = ['fan_data', 'coil_data', 'gas_heater_data', 'evap_pack_data'];

/** value → extra 'force-ahu project' (menu.tmpl:15 ifCompare chain). */
const FORCE_AHU_PROJECT = ['unit_technical_data', 'pressure_drop_calc', 'electrical_calc'];
/** value → ' no-ahu' (Shipping/Service Order). */
const NO_AHU = ['shipping_order', 'service_order'];
/** value → ' no-project' (Terms & Conditions ×3 + Guide Specifications ×3). */
const NO_PROJECT = [
  'sales_terms',
  'shipping_terms',
  'warranty_terms',
  'ahu_guide_specs',
  'condensing_unit_guide_specs',
  'packaged_unit_guide_specs',
];
/** value → ' no-project-selector' (Integrated Project Ledger; dev-sync I adds
 *  coil_analytics — menu.tmpl:15,30 on merged dev). */
const NO_PROJECT_SELECTOR = ['integrated_project_ledger', 'coil_analytics'];

/**
 * Exact class token list of a legacy menu button (menu.tmpl:15,30):
 * `report_btn {{type}}` + the hardcoded special cases + `{{value}}` last.
 * (weight_calc arrives here already re-typed to 'ahu' — reportsBoardApi
 * transformResponse, mirroring menu.js:54.)
 */
export function reportBtnClasses(r: IReportType): string[] {
  // r.type can be multi-word ('ahu project' — bill_of_materials, proposal,
  // ca_*): the legacy interpolates it into the class attribute where each
  // word is its own class token (menu.tmpl:15), so split before pushing —
  // otherwise hasScope('ahu')/hasScope('project') miss and canGenerate
  // wrongly disables Generate for those reports.
  const cls = ['report_btn', ...r.type.split(/\s+/).filter(Boolean)];
  if (r.type === 'project') cls.push('ahu', 'force-ahu');
  if (r.value === 'proposal') cls.push('force-ahu');
  if (FORCE_AHU_PROJECT.includes(r.value)) cls.push('force-ahu', 'project');
  if (NO_AHU.includes(r.value)) cls.push('no-ahu');
  if (NO_PROJECT.includes(r.value)) cls.push('no-project');
  if (NO_PROJECT_SELECTOR.includes(r.value)) cls.push('no-project-selector');
  cls.push(r.value);
  return cls;
}

/** jQuery-hasClass equivalent on the computed button class list. */
export function hasScope(report: IReportType | null | undefined, cls: string): boolean {
  if (!report) return false;
  return reportBtnClasses(report).includes(cls);
}

/** getReportType (main_header.js:1041-1048) — 'ahu' wins for project-type
 *  reports too, because they carry "project ahu force-ahu". */
export function getReportType(
  report: IReportType | null | undefined
): 'ahu' | 'project' | 'all_projects' | 'usage' | null {
  if (!report) return null;
  if (hasScope(report, 'ahu')) return 'ahu';
  if (hasScope(report, 'project')) return 'project';
  if (hasScope(report, 'all_projects')) return 'all_projects';
  if (hasScope(report, 'usage')) return 'usage';
  return null;
}

/** "Real" AHU-scoped report: .ahu without .force-ahu (main_header.js:1054). */
export function isAhuReport(report: IReportType | null | undefined): boolean {
  return hasScope(report, 'ahu') && !hasScope(report, 'force-ahu');
}

/**
 * The Generate-button truth table — main_header.js render(), BOTH branches:
 * project selected (:193-222) and no project (:299-326). `ahusCount` is the
 * full fetched AHU collection length (mains + versions), matching
 * `self.ahu.length > 0`.
 *
 * Legacy pre-step (:186-192, :290-298): with no/zero project, a selected AHU
 * is forced back to "0" before the table runs.
 */
export function computeCanGenerate(
  report: IReportType | null | undefined,
  projectId: string | null,
  ahuId: string | null,
  ahusCount: number
): boolean {
  if (!report) return false;
  let aId = ahuId;
  if ((!projectId || projectId === '0') && aId) aId = '0';
  const hasProject = !!projectId && projectId !== '0';
  const noAhuSelected = !aId || aId === '0';

  if (projectId != null) {
    // Branch A — a project id is present (main_header.js:193-222).
    if (hasScope(report, 'all_projects') || hasScope(report, 'usage') || hasScope(report, 'no-project')) {
      return true;
    }
    if (hasScope(report, 'project') && hasProject && noAhuSelected) return true;
    if (
      hasScope(report, 'ahu') &&
      hasProject &&
      ((!!aId && aId !== '0') || ahusCount > 0)
    ) {
      return true;
    }
    return false;
  }

  // Branch B — no project selected (main_header.js:299-326).
  if (hasScope(report, 'all_projects') || hasScope(report, 'no-project')) return true;
  if (
    (hasScope(report, 'project') && hasProject && noAhuSelected) ||
    hasScope(report, 'usage') ||
    hasScope(report, 'all_projects')
  ) {
    return true;
  }
  if (hasScope(report, 'ahu') && !!projectId && !!aId && aId !== '0') return true;
  return false;
}

export type EmptyBoardMessage =
  | 'can-generate'
  | 'select-project-msg'
  | 'select-project-ahu-msg'
  | 'select-report';

/**
 * Empty-board message selection — controller/action.js emptyBoard() :26-59.
 * `canGenerate` here is the LIVE value: for real AHU reports the multi-select
 * recomputes it as hasProject && selectedAhuIds.length > 0
 * (main_header.js:_triggerEmptyBoard :640-649); everything else uses the
 * render-time truth table.
 */
export function computeEmptyBoardMessage(
  report: IReportType | null | undefined,
  canGenerate: boolean,
  projectId: string | null,
  selectedAhuIds: number[]
): EmptyBoardMessage {
  if (canGenerate) return 'can-generate';
  const hasProject = !!projectId && projectId !== '0';
  const hasAhu = selectedAhuIds.length > 0;
  const ahuRep = isAhuReport(report);
  let reportType: string | null = getReportType(report);
  if (!ahuRep && reportType === 'ahu') reportType = 'project'; // action.js:51
  if (reportType === 'project' && !hasProject) return 'select-project-msg';
  if (ahuRep && (!hasProject || !hasAhu)) return 'select-project-ahu-msg';
  return 'select-report';
}

export interface ConditionalUI {
  projectsVisible: boolean;
  projectsHighlight: boolean;
  ahuVisible: boolean;
}

/**
 * applyConditionalUI (main_header.js:1050-1121) as a pure derivation.
 *
 * NOTE the legacy ahu-wrapper highlight is dead code: `isAHUandProjectReport
 * === isAhuReport`, and :1118-1120 strips the highlight for every AHU report
 * right after :1092-1094 adds it — so the AHU selector is NEVER highlighted.
 * We reproduce the net effect (no ahu highlight) rather than the dead branch.
 */
export function computeConditionalUI(
  report: IReportType | null | undefined,
  projectId: string | null
): ConditionalUI {
  const hasProject = !!projectId && projectId !== '0';
  const ahuRep = isAhuReport(report);
  const isProjectReport = hasScope(report, 'project');
  const isNoAhuReport = hasScope(report, 'no-ahu');
  const isNoProjectReport = hasScope(report, 'no-project');
  const isNoProjectSelector = hasScope(report, 'no-project-selector');
  const reportType = getReportType(report);

  // no-project AND no-project-selector both hide BOTH selectors
  // (main_header.js:1075-1085 — the code hides .ahu-wrapper for
  // no-project-selector too, despite the comment saying otherwise).
  if (isNoProjectReport || isNoProjectSelector) {
    return { projectsVisible: false, projectsHighlight: false, ahuVisible: false };
  }
  if (ahuRep) {
    return { projectsVisible: true, projectsHighlight: !hasProject, ahuVisible: true };
  }
  if (isProjectReport) {
    return {
      projectsVisible: true,
      projectsHighlight: !hasProject,
      ahuVisible: !isNoAhuReport,
    };
  }
  return {
    projectsVisible: true,
    projectsHighlight:
      (reportType === 'project' || reportType === 'ahu') && !hasProject,
    ahuVisible: false,
  };
}

/** current-report → the `reports` flag key that decides whether an AHU is
 *  eligible for this report. An ineligible row is disabled and unchecked in
 *  the multi-select, left out of the generate dispatch, and not counted
 *  towards the board's "something is selected" state.
 *
 *  Four section reports (main_header.tmpl:23-33 is_fan_report/… branches) ask
 *  "does the AHU have this section at all". `fmi_assembly` and `ahu_acoustics`
 *  instead read a *generatable* flag — having the section is not enough. */
export function sectionFlagKey(
  reportValue: string | null
): keyof IReportAhuRow['reports'] | null {
  switch (reportValue) {
    case 'fan_data':
      return 'has_fan_section';
    case 'coil_data':
      return 'has_coil_section';
    case 'gas_heater_data':
      return 'has_gas_heater_section';
    case 'evap_pack_data':
      return 'has_evap_pack_section';
    // ⚠ dev-sync G / CB-2 — VERIFIED BY INSPECTION ONLY, NEVER EXECUTED.
    // The fifth section-report branch (main_header.tmpl:23,25,29,31). Note it
    // reads `reports.fmi_assembly` — the *generatable* flag — where the other
    // four read a `has_*_section` flag. Belt-and-braces in practice: by the
    // time a row reaches the multi-select, filterAhusForCurrentReport below
    // has already REMOVED every ineligible row, so this can only ever return
    // a key for rows that pass. UNTESTABLE on dev3: no
    // `ksdm_master_ahu_report` row for value='fmi_assembly'.
    case 'fmi_assembly':
      return 'fmi_assembly';
    // AHU Acoustics is built from the fans' octave-band sound power, which only
    // exists once a fan has been selected and rated — an AHU without one would
    // render a report of em-dashes. `SoundPowerLevelAHUView` applies the same
    // rule server-side, which is what covers a stale list (this one is fetched
    // once per project and never revalidated) and a hash AHU that isn't in it.
    case 'ahu_acoustics':
      return 'ahu_acoustics';
    default:
      return null;
  }
}

/** True if `row` may be generated for the current report — the `sectionFlagKey`
 *  flag, or unconditionally for a report that gates on nothing. A row that is
 *  not in the fetched list at all counts as eligible, matching the legacy
 *  snapshot, which could only ever skip rows it had rendered. */
export function isAhuEligibleForReport(
  row: IReportAhuRow | undefined,
  reportValue: string | null
): boolean {
  const flagKey = sectionFlagKey(reportValue);
  if (!flagKey) return true;
  return row ? !!row.reports?.[flagKey] : true;
}

/**
 * `ids` minus the AHUs this report cannot be generated for.
 *
 * The legacy board re-snapshotted the multi-select from the DOM excluding
 * disabled rows at generate time (main_header.js:516-519), but its
 * render-time `selected_ahu_ids` default is every main AHU — disabled rows
 * included. So the board could report "ready to generate" for a project whose
 * every AHU is ineligible, and then dispatch either nothing or, on a project
 * with exactly one AHU (which the board auto-selects into the hash), that one
 * ineligible AHU. Running the same filter everywhere the selection is read
 * closes both.
 */
export function eligibleAhuIds(
  ids: number[],
  rows: IReportAhuRow[],
  reportValue: string | null
): number[] {
  if (!sectionFlagKey(reportValue)) return ids; // identity: keeps memo deps stable
  return ids.filter((id) =>
    isAhuEligibleForReport(
      rows.find((a) => a.id === id),
      reportValue
    )
  );
}

/**
 * The header Generate button's enabled state: the legacy truth table
 * (`computeCanGenerate`), and for a report that gates AHUs
 * (`sectionFlagKey`) at least one eligible AHU in the fetched list.
 *
 * Without the second clause the button stayed enabled on a project whose
 * every AHU is ineligible — there is nothing it could generate, and the
 * legacy table only counts `ahus.length`. Deliberately list-level, not
 * selection-level: the legacy header button never tracked the multi-select
 * (main_header.js:184-222 runs on re-render only), and the AHU list is one of
 * its re-render triggers. The empty board is what reflects the live picks
 * (`computeEmptyBoardMessage`).
 */
export function computeHeaderCanGenerate(
  canGenerate: boolean,
  rows: IReportAhuRow[],
  reportValue: string | null
): boolean {
  if (!canGenerate) return false;
  if (!sectionFlagKey(reportValue)) return true;
  return rows.some((row) => isAhuEligibleForReport(row, reportValue));
}

/**
 * ⚠ dev-sync G / CB-2 — VERIFIED BY INSPECTION ONLY, NEVER EXECUTED.
 *
 * _filterAhusForCurrentReport (main_header.js:69-76). FMI is the one report
 * that *removes* ineligible AHUs from the list outright instead of merely
 * disabling their rows — an AHU with no plenum-arr3/arr4-or-DWDI-arr3 fan that
 * has a motor simply does not appear. Every other report value returns the
 * list unchanged (identity preserved, so callers' memo deps stay stable).
 *
 * The filter runs on the FLAT list before grouping, exactly like the legacy,
 * so an ineligible version row disappears from under an eligible main (and an
 * eligible version orphaned by an ineligible main is dropped by
 * groupAhusByMain, which only emits versions hanging off a surviving main —
 * same as legacy).
 *
 * UNTESTABLE on dev3: there is no `ksdm_master_ahu_report` row for
 * value='fmi_assembly' (no migration in the delta creates one), so
 * currentReportValue can never be 'fmi_assembly' and this always short-
 * circuits on the first line. Ported so the board is correct the day the
 * master row is seeded.
 */
export function filterAhusForCurrentReport(
  rows: IReportAhuRow[] | undefined,
  currentReportValue: string | null
): IReportAhuRow[] {
  if (currentReportValue !== 'fmi_assembly') {
    return rows || [];
  }
  return (rows || []).filter((row) => !!row?.reports?.fmi_assembly);
}

/** A main AHU with its (sorted) version rows — _groupAhusByMain
 *  (main_header.js:35-58): versions sorted by version_order (nulls → 0) then id. */
export interface IGroupedAhu extends IReportAhuRow {
  versions: IReportAhuRow[];
}

export function groupAhusByMain(rows: IReportAhuRow[] | undefined): IGroupedAhu[] {
  const mains: IReportAhuRow[] = [];
  const byParent: Record<number, IReportAhuRow[]> = {};
  (rows || []).forEach((r) => {
    if (r.parent_version_id) {
      (byParent[r.parent_version_id] || (byParent[r.parent_version_id] = [])).push(r);
    } else {
      mains.push(r);
    }
  });
  Object.keys(byParent).forEach((pid) => {
    byParent[Number(pid)].sort((a, b) => {
      const ao = a.version_order == null ? 0 : a.version_order;
      const bo = b.version_order == null ? 0 : b.version_order;
      if (ao !== bo) return ao - bo;
      return a.id - b.id;
    });
  });
  return mains.map((m) => ({ ...m, versions: byParent[m.id] || [] }));
}

// ---- Deep-link hash `#report*project*ahu` (menu.js:45-47, main_header.js:104-119,
//      router.js '' / ':tab' → open:report) ----

export interface HashState {
  report: string | null;
  project: string | null;
  ahu: string | null;
}

export function parseHash(hash: string): HashState {
  const fragment = hash.replace(/^#/, '');
  const parts = fragment.split('*');
  return {
    report: parts[0] ? parts[0] : null,
    project: parts[1] ? parts[1] : null,
    ahu: parts[2] ? parts[2] : null,
  };
}

/** Backbone.history.navigate equivalent — set the hash (adds a history entry,
 *  hashchange re-parses, mirroring the legacy router round-trip).
 *  `replace` swaps the current entry instead (still fires hashchange). */
export function navigateHash(fragment: string, { replace = false } = {}): void {
  const clean = fragment.replace(/\*\*/g, '*'); // menu.js:105 replaceAll('**','*')
  if (window.location.hash.replace(/^#/, '') !== clean) {
    if (replace) window.location.replace(`#${clean}`);
    else window.location.hash = clean;
  }
}
